import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Nav from './Nav';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
import { company, legalPaths } from '../data/company';

export interface TocEntry {
  id: string;
  label: string;
}

interface LegalLayoutProps {
  title: string;
  kicker: string;
  intro: string;
  toc: TocEntry[];
  children: React.ReactNode;
}

const RELATED = [
  { to: legalPaths.privacy, label: 'Privacy Policy' },
  { to: legalPaths.cookies, label: 'Cookie Policy' },
  { to: legalPaths.terms, label: 'Terms of Use' },
];

/** Shared shell for the three legal documents: header, sticky contents, prose. */
const LegalLayout: React.FC<LegalLayoutProps> = ({ title, kicker, intro, toc, children }) => (
  <div className="min-h-screen bg-ink-950">
    <Nav />

    <header className="texture-field relative overflow-hidden border-b border-white/10 pt-[72px]">
      <div className="bloom -left-32 top-0 h-[420px] w-[420px] bg-peri-600/16" />
      <div className="relative mx-auto max-w-edge px-6 py-16 lg:px-10 lg:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-label text-paper/55 transition-colors hover:text-paper"
        >
          <ArrowLeft size={13} strokeWidth={1.8} />
          Back to Parnasa Apps
        </Link>

        <div className="mt-10 flex items-center gap-3">
          <span className="font-mono text-[11px] tracking-label text-peri-400">
            <FileText size={13} strokeWidth={1.7} />
          </span>
          <span className="label">{kicker}</span>
        </div>

        <h1 className="display-tight mt-5 text-[44px] text-paper sm:text-[60px] lg:text-[68px]">
          {title}
        </h1>
        <p className="mt-6 max-w-2xl text-[16.5px] leading-relaxed text-paper/60">{intro}</p>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
          <p className="font-mono text-[10.5px] uppercase tracking-label text-paper/55">
            Last updated: {company.legalUpdated}
          </p>
          <p className="font-mono text-[10.5px] uppercase tracking-label text-paper/55">
            Controller: {company.legalName} · {company.address.city}, {company.address.country}
          </p>
        </div>
      </div>
    </header>

    <main className="mx-auto max-w-edge px-6 py-16 lg:px-10 lg:py-20">
      <div className="grid gap-14 lg:grid-cols-[240px_1fr] lg:gap-20">
        <aside className="lg:sticky lg:top-[100px] lg:self-start">
          <p className="label">Contents</p>
          <nav className="mt-5 space-y-2.5" aria-label="Document contents">
            {toc.map((entry, i) => (
              <a
                key={entry.id}
                href={`#${entry.id}`}
                className="group flex items-baseline gap-3 text-[13.5px] leading-snug text-paper/55 transition-colors hover:text-paper"
              >
                <span className="font-mono text-[10px] text-paper/55 group-hover:text-peri-400">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {entry.label}
              </a>
            ))}
          </nav>

          <div className="mt-10 border-t border-white/10 pt-6">
            <p className="label">Related</p>
            <div className="mt-4 space-y-2.5">
              {RELATED.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block text-[13.5px] text-paper/55 transition-colors hover:text-paper"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </aside>

        <article className="prose-legal min-w-0 max-w-3xl">{children}</article>
      </div>
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default LegalLayout;
